import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Bitte geben Sie Ihre E-Mail-Adresse ein');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/auth/request-password-reset', { email });
      setSent(true);
      toast.success('Falls ein Konto existiert, wurde ein Reset-Link versendet');
    } catch (error) {
      // Still show success to prevent email enumeration
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md" data-testid="forgot-password-success">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">E-Mail versendet!</h2>
            <p className="text-gray-600 mb-2">
              Falls ein Konto mit dieser E-Mail-Adresse existiert, erhalten Sie in Kürze einen Link zum Zurücksetzen Ihres Passworts.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Bitte prüfen Sie auch Ihren Spam-Ordner.
            </p>
            <Link to="/login">
              <Button variant="outline" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zurück zur Anmeldung
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="forgot-password-form">
        <CardHeader className="text-center">
          <div className="w-12 h-12 bg-[#9B1B30]/10 rounded-full flex items-center justify-center mx-auto mb-2">
            <Mail className="w-6 h-6 text-[#9B1B30]" />
          </div>
          <CardTitle className="text-xl">Passwort vergessen?</CardTitle>
          <CardDescription>
            Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-Mail-Adresse</Label>
              <Input 
                id="email"
                type="email" 
                placeholder="ihre@email.de"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="forgot-email-input"
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-[#9B1B30] hover:bg-[#7A1525]"
              disabled={loading}
              data-testid="forgot-submit-btn"
            >
              {loading ? 'Wird gesendet...' : 'Reset-Link anfordern'}
            </Button>
            
            <div className="text-center">
              <Link 
                to="/login" 
                className="text-sm text-[#9B1B30] hover:underline inline-flex items-center"
              >
                <ArrowLeft className="w-3 h-3 mr-1" />
                Zurück zur Anmeldung
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
